import React from 'react';
import { motion } from 'framer-motion';
export function FoundersAnatomyOfEndurance() {
  return <div className="min-h-screen bg-gradient-to-b from-black via-[#0a1f2e] to-black" data-id="element-498">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-id="element-499">
        <div className="absolute inset-0" data-id="element-500">
          <img src="https://cdn.magicpatterns.com/uploads/hG6wRmeXbcpGUWshNGVPem/9_Descending_Jacobs_Ladder.jpg" alt="Descending Jacob's Ladder" className="w-full h-full object-cover" data-id="element-501" />
          <div className="absolute inset-0 bg-black/70" data-id="element-502" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-503">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-504">
            Founder Story: The Anatomy of Endurance
          </motion.p>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-6xl md:text-8xl font-bold text-white mb-8" data-id="element-505">
            What does it take?
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-2xl text-gray-400 max-w-3xl mx-auto" data-id="element-506">
            To run 240 miles. To build the almost impossible.
          </motion.p>
        </div>
      </section>

      {/* Trait 1: Conviction */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-507">
        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative h-full min-h-[600px]" data-id="element-508">
          <img src="https://cdn.magicpatterns.com/uploads/rmRBKPFnK2E6dvb91taoLe/10_SunsettingCanyon.jpg" alt="Canyon at Sunset" className="w-full h-full object-cover" data-id="element-509" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50" data-id="element-510" />
        </motion.div>

        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="p-8 md:p-16 bg-gradient-to-r from-black/50 to-black" data-id="element-511">
          <div className="max-w-xl" data-id="element-512">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-513">
              Trait 01
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-514">Conviction</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-515">
              <p className="text-xl" data-id="element-516">
                "I am a high conviction leader. I see opportunity in chaos and
                go after seemingly intransigent challenges."
              </p>
              <p data-id="element-517">
                That conviction is encoded in DNA. My grandparents were shipped
                to Siberian labor camps and came to the United States as
                refugees to start over. I come by my relentless drive and
                endurance honestly.
              </p>
              <p data-id="element-518">
                This experience shapes every investment decision, every founder
                conversation, every nearly impossible goal we pursue.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-519">
              <p className="text-white font-semibold mb-3" data-id="element-520">Heritage → Action</p>
              <p className="text-gray-400 text-sm" data-id="element-521">
                From Siberian labor camps to building a $75M global fund
                addressing forced labor risks. The circle completes itself
                through conviction.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Trait 2: Resilience */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-522">
        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="p-8 md:p-16 bg-gradient-to-l from-black/50 to-black order-2 lg:order-1" data-id="element-523">
          <div className="max-w-xl ml-auto" data-id="element-524">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-525">
              Trait 02
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-526">Resilience</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-527">
              <p className="text-xl" data-id="element-528">
                I spent 10 years building and deploying a $75M global fund in
                the face of political headwinds from conception to getting money
                on the ground.
              </p>
              <p data-id="element-529">
                Resilience requires navigating when the path disappears. It's
                conducting risk assessments in Myanmar, Indonesia, the
                Philippines, and India. It's leading organizational
                transformation through CEO transition and reduction of force
                post-Covid.
              </p>
              <p data-id="element-530">
                It's standing at the Moab 240 start line knowing David Goggins—a
                former Navy SEAL—DNF'd this race on his first attempt. And
                finishing anyway. 8th place female.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-531">
              <p className="text-white font-semibold mb-3" data-id="element-532">
                10-Year Track Record
              </p>
              <ul className="space-y-2 text-gray-400 text-sm" data-id="element-533">
                <li data-id="element-534">
                  • $75M raised from family offices, foundations, governments
                </li>
                <li data-id="element-535">• 40 projects deployed globally</li>
                <li data-id="element-536">
                  • Led through political headwinds and organizational
                  transformation
                </li>
                <li data-id="element-537">• Served as Director, Managing Director, and President</li>
                <li data-id="element-538">
                  • Managed global teams through audits, reporting to diverse
                  funders, and delivering on KPIs
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative h-full min-h-[600px] order-1 lg:order-2" data-id="element-539">
          <img src="https://cdn.magicpatterns.com/uploads/jFhgLpSVhVCK824V85YJZE/1_Moab_Start_line.jpg" alt="Moab 240 Start Line" className="w-full h-full object-cover" data-id="element-540" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/50" data-id="element-541" />
        </motion.div>
      </section>

      {/* Trait 3: Execution */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-542">
        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative h-full min-h-[600px]" data-id="element-543">
          <img src="https://cdn.magicpatterns.com/uploads/mworPtjEMYJXiYJ2sRbKWX/2_LeadvilleFinish.jpg" alt="Leadville 100 Finish" className="w-full h-full object-cover" data-id="element-544" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/50" data-id="element-545" />

          {/* Image overlay text */}
          <div className="absolute bottom-8 left-8 right-8" data-id="element-546">
            <p className="text-white text-sm mb-1" data-id="element-547">
              Leadville 100 Mile Endurance Run
            </p>
            <p className="text-white/60 text-xs" data-id="element-548">
              Top 30 Female Finisher • 30+ Hour Race
            </p>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="p-8 md:p-16 bg-gradient-to-r from-black/50 to-black" data-id="element-549">
          <div className="max-w-xl" data-id="element-550">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-551">
              Trait 03
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-552">Execution</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-553">
              <p className="text-xl" data-id="element-554">
                Conviction without execution is just noise. Resilience without
                results is just stubbornness.
              </p>
              <p data-id="element-555">
                I successfully completed the Moab 240-miler. Top 30 female
                finisher at Leadville 100. LifeTime Foundation athlete. These
                are examples of execution under extreme conditions.
              </p>
              <p data-id="element-556">
                In fund management: developed criteria, diligence processes, and
                review systems. Deployed $75M across 40 projects. Reported to
                Board of Directors. Engaged auditors. Led organizational change.
                Delivered results.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-557">
              <p className="text-white font-semibold mb-3" data-id="element-558">
                Execution = Results
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-559">
                <div data-id="element-560">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-561">
                    240
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-562">Miles Completed</p>
                </div>
                <div data-id="element-563">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-564">
                    $75M
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-565">Capital Deployed</p>
                </div>
                <div data-id="element-566">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-567">
                    Top 30
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-568">Leadville Finish</p>
                </div>
                <div data-id="element-569">
                  <p className="text-3xl font-bold text-vitalityTeal mb-1" data-id="element-570">
                    40
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-571">Projects Funded</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Trait 4: Vision */}
      <section className="min-h-screen grid lg:grid-cols-2 items-center" data-id="element-572">
        {/* Content */}
        <motion.div initial={{
        opacity: 0,
        x: -50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="p-8 md:p-16 bg-gradient-to-l from-black/50 to-black order-2 lg:order-1" data-id="element-573">
          <div className="max-w-xl ml-auto" data-id="element-574">
            <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-575">
              Trait 04
            </p>
            <h2 className="text-5xl font-bold text-white mb-6" data-id="element-576">Vision</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed" data-id="element-577">
              <p className="text-xl" data-id="element-578">
                Applying an ultrarunner mindset to investing in a world of
                vitality.
              </p>
              <p data-id="element-579">
                Based on the Biophilia Hypothesis and tapping into backgrounds
                in biology, molecular genetics, and corporate sustainability,
                we're building Vitality—a fund that invests in a vibrant planet
                and human health through biomimicry.
              </p>
              <p data-id="element-580">
                This is the long run. Not quarters. Not years. Decades. Building
                a multidisciplinary, unconventional team for challenges that
                don't have obvious solutions yet.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/10" data-id="element-581">
              <p className="text-white font-semibold mb-3" data-id="element-582">Foundation</p>
              <ul className="space-y-2 text-gray-400 text-sm" data-id="element-583">
                <li data-id="element-584">• Biology & Molecular Genetics background</li>
                <li data-id="element-585">• Executive MBA, Duke University</li>
                <li data-id="element-586">• MPP, University of Chicago</li>
                <li data-id="element-587">• Forte Fellow</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Image */}
        <motion.div initial={{
        opacity: 0,
        x: 50
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative h-full min-h-[600px] order-1 lg:order-2" data-id="element-588">
          <img src="https://cdn.magicpatterns.com/uploads/hG6wRmeXbcpGUWshNGVPem/9_Descending_Jacobs_Ladder.jpg" alt="Descending Jacob's Ladder" className="w-full h-full object-cover" data-id="element-589" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/50" data-id="element-590" />
        </motion.div>
      </section>

      {/* Closing Statement */}
      <section className="py-32 bg-black" data-id="element-591">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-592">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-593">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-594">
              Natalya Wallin
            </h2>
            <p className="text-xl text-vitalityTeal mb-6" data-id="element-595">
              GP & Managing Director, Vitality VC
            </p>
            <p className="text-2xl text-gray-300 leading-relaxed max-w-3xl mx-auto" data-id="element-596">
              Conviction. Resilience. Execution. Vision.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}